import { AxiosError } from 'axios';
import constate from 'constate';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiStatus, NotificationPosition } from 'core/enums';
import { INotification, INotificationContent } from 'core/types';

const useNotification = () => {
  const { t } = useTranslation();

  const [notification, setNotificationState] = useState<INotification | null>(null);

  // Written with the state, not after render: a clear in the same handler as a show must
  // compare against the notification just set.
  const notificationRef = useRef(notification);

  const setNotification = useCallback((notification: INotification | null) => {
    notificationRef.current = notification;
    setNotificationState(notification);
  }, []);

  // The slot as it stands now, for work that outlives the render (or component) that started it.
  const getNotification = useCallback(() => notificationRef.current, []);

  const getIsLoadingNotificationDisplayed = useCallback(
    (position = NotificationPosition.TOP) =>
      ApiStatus.LOADING === notification?.type && position === notification?.position,
    [notification]
  );

  const isLoadingTopNotificationDisplayed = getIsLoadingNotificationDisplayed();

  const showNotification = useCallback(
    (notification: INotification) => {
      if (notification) {
        setNotification(notification);
      }
    },
    [setNotification]
  );

  const showLoadingNotification = useCallback(
    (notificationContent: INotificationContent, notificationPosition: NotificationPosition) =>
      setNotification({
        title: notificationContent?.title || t('titleStatus'),
        message: notificationContent.message,
        buttons: notificationContent?.buttons,
        type: ApiStatus.LOADING,
        position: notificationPosition,
      }),
    [t, setNotification]
  );

  const showWarningNotification = useCallback(
    (notificationContent: INotificationContent, notificationPosition: NotificationPosition) =>
      setNotification({
        ...notificationContent,
        title: notificationContent?.title || t('titleWarning'),
        message: notificationContent.message,
        type: ApiStatus.WARNING,
        position: notificationPosition,
      }),
    [t, setNotification]
  );

  const showSuccessNotification = useCallback(
    (notificationContent: INotificationContent, notificationPosition: NotificationPosition) =>
      setNotification({
        title: notificationContent?.title || t('titleSuccess'),
        message: notificationContent.message,
        buttons: notificationContent?.buttons,
        type: ApiStatus.SUCCESS,
        position: notificationPosition,
        disableAutoClose: notificationContent?.disableAutoClose || false,
      }),
    [t, setNotification]
  );

  const showErrorNotification = useCallback(
    (
      notificationContent: INotificationContent,
      notificationPosition: NotificationPosition,
      error?: AxiosError
    ) => {
      setNotification({
        title: notificationContent?.title || t('titleError'),
        message:
          notificationContent?.message || (error?.response?.data as any)?.error || t('serverError'),
        buttons: notificationContent?.buttons,
        type: ApiStatus.ERROR,
        position: notificationPosition,
        disableAutoClose: notificationContent?.disableAutoClose || false,
      });
    },
    [t, setNotification]
  );

  const clearNotification = useCallback(() => {
    setNotification(null);
  }, [setNotification]);

  const clearFormNotification = useCallback(() => {
    if (notificationRef.current?.position === NotificationPosition.FORM) {
      clearNotification();
    }
  }, [clearNotification]);

  const clearDrawerNotification = useCallback(() => {
    if (notificationRef.current?.position === NotificationPosition.DRAWER) {
      clearNotification();
    }
  }, [clearNotification]);

  const clearNotificationTimeout = useCallback(() => {
    setTimeout(() => {
      if (notificationRef.current?.disableAutoClose) {
        return;
      }

      clearNotification();
    }, 1000);
  }, [clearNotification]);

  return {
    notification,
    getNotification,
    showNotification,
    getIsLoadingNotificationDisplayed,
    isLoadingTopNotificationDisplayed,
    showLoadingNotification,
    showWarningNotification,
    showSuccessNotification,
    showErrorNotification,
    clearFormNotification,
    clearDrawerNotification,
    clearNotification,
    clearNotificationTimeout,
  };
};

export const [NotificationProvider, useNotificationContext] = constate(useNotification);
